import { useMemo } from 'react';
import dayjs from 'dayjs';

import { ESubscriptionStatus, TSubscription } from '@mopla/data-models';

import { TInfoMessageConfig } from './types';

export const useDTicketInfoMessageConfig = (
	subscription: TSubscription
): TInfoMessageConfig => {
	const infoMessageState = useMemo(() => {
		if (!subscription) return null;

		// The backend forbids revoking the cancellation (e.g. the subscription was
		// cancelled by an admin or payment failed) — hide the links leading into the edit-pause and
		// edit-subscription flows, which are unavailable in that state.
		const showActionLink = !subscription.disallowCancelCancellation;

		const isPauseEndSameAsCanceledAt = dayjs(subscription.pauseEnd)
			.add(1, 'day')
			.isSame(dayjs(subscription.canceledAt));

		const isPauseStartSameAsCanceledAt = dayjs(subscription.pauseStart).isSame(
			dayjs(subscription.canceledAt)
		);

		// Paused subscription + Cancellation scheduled,
		// but pauseEnd and cancelledAt fall on the same date
		// "Your Deutschlandticket subscription is currently paused and will be cancelled on {canceledAt}."
		if (
			subscription.status === ESubscriptionStatus.PAUSED &&
			subscription.pauseEnd &&
			subscription.canceledAt &&
			isPauseEndSameAsCanceledAt
		) {
			return {
				type: 'paused-with-cancellation-scheduled-same-date' as const,
				canceledAt: subscription.canceledAt,
			};
		}

		// Paused subscription + Cancellation scheduled (PAUSED + canceledAt)
		// "Your Deutschlandticket subscription is paused until {pauseEnd} and will be cancelled on {canceledAt}."
		if (
			subscription.status === ESubscriptionStatus.PAUSED &&
			subscription.pauseEnd &&
			subscription.canceledAt
		) {
			return {
				type: 'paused-with-cancellation-scheduled' as const,
				pauseEnd: subscription.pauseEnd,
				canceledAt: subscription.canceledAt,
			};
		}

		// Pause scheduled + Cancellation scheduled (SCHEDULED + canceledAt)
		// "Your Deutschlandticket subscription will be paused from {pauseStart} and cancelled on {canceledAt}."
		if (
			subscription.status === ESubscriptionStatus.ACTIVE &&
			subscription.pauseStart &&
			subscription.canceledAt &&
			!isPauseStartSameAsCanceledAt
		) {
			return {
				type: 'pause-scheduled-with-cancellation-scheduled' as const,
				pauseStart: subscription.pauseStart,
				canceledAt: subscription.canceledAt,
			};
		}
		// Cancellation scheduled (ACTIVE + canceledAt)
		if (
			subscription.status === ESubscriptionStatus.ACTIVE &&
			subscription.canceledAt
		) {
			return {
				type: 'cancellation-scheduled' as const,
				date: subscription.canceledAt,
				showActionLink,
			};
		}

		// Pause scheduled (ACTIVE + pauseStart + pauseEnd)
		if (
			subscription.status === ESubscriptionStatus.ACTIVE &&
			subscription.pauseStart &&
			subscription.pauseEnd
		) {
			return {
				type: 'pause-scheduled' as const,
				pauseStart: subscription.pauseStart,
				pauseEnd: subscription.pauseEnd,
				showActionLink,
			};
		}

		// Paused subscription - show next debit date (pauseEnd)
		if (
			subscription.status === ESubscriptionStatus.PAUSED &&
			subscription.pauseEnd
		) {
			return {
				type: 'paused' as const,
				date: dayjs(subscription.pauseEnd).add(1, 'day'),
			};
		}

		return null;
	}, [subscription]);

	return infoMessageState;
};
